import { Student } from './student'
import { Teacher } from './teacher'

export enum CompetitionMode {
  Video = 'video',
  Online = 'normal',
}

export enum CompetitionListTabItemName {
  School = 'school',
  Online = 'online',
  Region = 'region',

  RegionAvailable = 'regionAvailable',
  RegionParticipated = 'regionParticipated',
}

export enum CompetitionRegionDetailsTabItemName {
  None = 'none',
  Stats = 'stats',
  General = 'general',
}

export interface CompetitionType {
  competition_type_id: number
  category: string
  item_name: string
  item_tag: string
  title: string
  description: string
  rules: string[]
  head_img_url: string
}

// 'own_grades' 用于支持所在年级的选择，服务端不存在该值。
// 它是 custom 的一个子集，即 clazzes 为空的情况。
// 注意在保存和编辑的时需要进行转换。
export type CompetitionScopeType = 'all_school' | 'own_grades' | 'own_classes' | 'custom'

export interface CompetitionScopeSchool {
  school_id: number
}

export interface CompetitionScopeGrade {
  grade_id: number
  grade_no: number
  grade_name: string
}

export interface CompetitionScopeClazz {
  id: number
  class_no: number
  grade_id: number
  grade_no: number
  name: string
  note_name: string | null
}

export interface CompetitionScope {
  type: CompetitionScopeType
  schools: CompetitionScopeSchool[]
  grades: CompetitionScopeGrade[]
  clazzes: CompetitionScopeClazz[]
}

export interface CompetitionScopeConfig {
  schools: CompetitionScopeSchool[]
  own_simple_classes: CompetitionScopeClazz[]
  all_school_classes: CompetitionScopeClazz[]
  all_grades: CompetitionScopeGrade[]
}

type CompetitionStatus = 'on' | 'off' | 'draft'
type TimeState = 'started' | 'un_start' | 'ended'

export interface Competition {
  competition: {
    competition_id: number
    competition_type: CompetitionType
    create_teacher: Teacher
    theme_title: string
    start_time: string
    end_time: string
    description: string
    status: CompetitionStatus
    time_state: TimeState
    take_part_limits: number
  }
  competition_scope: CompetitionScope
}

export interface CompetitionForm {
  competition: {
    competition_id?: number
    competition_type: CompetitionType
    create_teacher?: Teacher
    theme_title?: string
    start_time?: string
    end_time?: string
    description?: string
    status?: CompetitionStatus
    time_state?: TimeState
    take_part_limits?: number
  }
  competition_scope?: CompetitionScope
}

export interface CreateCompetitionForm {
  competition_id?: number | null
  competition_type_id: number | null
  theme_title: string | null
  title?: string | null
  start_time: string | Date | null
  end_time: string | Date | null
  take_part_limits: number | null
  description: string | null
  scope_type: string | null
  scope_school_ids: number[]
  scope_grade_ids: number[]
  scope_class_ids: number[]
}

export enum CompetitionCategory {
  RANK = '竞技排名赛',
  SCORE = '打卡积分赛',
  TEAM = '团体目标赛',
}

export interface CompetitionBaseConfig {
  id: string
  type: string
  title: string
  img_url: string
  description: string | null
  gender: string | null
  difficult_level: string
  orientation: string
  tag?: string
  subTag?: string
}

export interface CompetitionRankConfig extends CompetitionBaseConfig {
  total_take_limits: number | null
}

export interface CompetitionScoreConfig extends CompetitionBaseConfig {
  finish_score: number
  limit_score: number
}

export interface CompetitionTeamConfig extends CompetitionBaseConfig {
  target_score: number
  take_part_limits: number | null
}

export interface CompetitionLaunchPreview {
  name: string
  category: CompetitionCategory
  introduce: string | null
  startTime: string | Date | null
  endTime: string | Date | null
  scope: string
  items: string[]
  limitScore: number
  targetScore: number
  takePartLimits: number | null
}

export type CompetitionConfigUion = CompetitionRankConfig & CompetitionScoreConfig & CompetitionTeamConfig

export interface CompetitionItem<T extends CompetitionCategory, U extends CompetitionConfigUion> {
  name: T
  config: U[]
  introduce: string | null
  game_type: string | null
}

export type CompetitionConfig =
  | CompetitionItem<CompetitionCategory.RANK, CompetitionConfigUion>
  | CompetitionItem<CompetitionCategory.TEAM, CompetitionConfigUion>
  | CompetitionItem<CompetitionCategory.SCORE, CompetitionConfigUion>

export interface LaunchCompetitionForm {
  match_type: string
  introduce: string | null
  scope_type: string | null
  scope_school_ids: number[]
  scope_grade_ids: number[]
  scope_class_ids: number[]
  theme_title: string | null
  start_time: string | Date | null
  end_time: string | Date | null
  config: CompetitionConfig
}

export interface CompetitionOptions {
  title: CompetitionCategory
  describe: string
  img: string
}

type OnlineAddConfig = {
  competition_name: string | null
  name_check: boolean
  limits_check: boolean
  target_check: boolean
}

export type CompetitionOnlineConfig = {
  rank: CompetitionItem<CompetitionCategory.RANK, CompetitionConfigUion & OnlineAddConfig>
  score: CompetitionItem<CompetitionCategory.SCORE, CompetitionConfigUion & OnlineAddConfig>
  team: CompetitionItem<CompetitionCategory.TEAM, CompetitionConfigUion & OnlineAddConfig>
}

export type OnlineLaunchCompetitionForm = Omit<LaunchCompetitionForm, 'config'> & {
  config: CompetitionOnlineConfig
  rule: string | null
  stamp: string | null
  company: string | null
  match_img_url: string | null
}

export function formatScope(competition: Competition): string {
  const { competition_scope: scope } = competition
  const result = []

  if (scope.type === 'all_school') {
    result.push('全校')
  } else {
    scope.grades.forEach((g) => {
      result.push(`${g.grade_name}全部`)
    })

    scope.clazzes.forEach((c) => {
      result.push(c.name)
    })
  }

  return result.join('、')
}

export function formatTakePartLimits(competition: Competition): string {
  const takePartLimits = competition.competition.take_part_limits
  if (takePartLimits) {
    return `${takePartLimits}次`
  }

  return '不限'
}

export enum CompetitionRankingPeriod {
  All = 'all',
  Weekly = 'weekly',
  Monthly = 'monthly',
}

export interface CompetitionRankingItem {
  user_id: string
  count: number | string
  record_id: string
  clazz: { id: number; name: string; grade_id: number }
}

export interface CompetitionRankingBoard {
  students: { [key: string]: Student }
  students_stats: CompetitionRankingItem[]
  competition: { rank_h5_url: string }
}

export function getRankingUserInfo(rankingBoard: CompetitionRankingBoard, userId: string, key: keyof Student) {
  return rankingBoard?.students[userId]?.[key]
}

export interface CompetitionStatItem {
  student_amount: number
  participant: number
  participant_rate: number
  best_score: number
}

export interface CompetitionGradeStat {
  grades: { grade_id: number; grade_no: number; grade_name: string }[]
  grades_stats: {
    [key: number]: CompetitionStatItem
  }
}

export function getGradeStatValue(
  stat: CompetitionGradeStat,
  gradeId: number,
  key: keyof CompetitionStatItem
): number | null {
  return stat.grades_stats[gradeId]?.[key]
}

export interface CompetitionClassStat {
  classes: { id: number; name: string }[]
  classes_stats: {
    [key: number]: CompetitionStatItem
  }
}

export function getClassStatValue(
  stat: CompetitionClassStat,
  classId: number,
  key: keyof CompetitionStatItem
): number | null {
  return stat.classes_stats[classId]?.[key]
}
